import type { VisaBulletinData } from "@shared/schema";
import {
  VISA_BULLETIN_ANSWER,
  VISA_BULLETIN_QUESTION,
  buildVisaBulletinAsOfLine,
} from "@shared/aeo-copy";

/**
 * The public "Checked for updates" line. The fetcher runs once a day; a
 * timestamp older than this is a stalled check. Printing it tells a reader
 * the update path has been blocked, so the page and API omit it.
 */
export const BULLETIN_CHECK_FRESH_MS = 48 * 60 * 60 * 1000;

export function freshBulletinCheckAt(
  value: string | Date | null | undefined,
  now: Date = new Date(),
): string | null {
  if (value == null || value === "") return null;
  const d = value instanceof Date ? value : new Date(value);
  const t = d.getTime();
  if (Number.isNaN(t)) return null;
  if (now.getTime() - t > BULLETIN_CHECK_FRESH_MS) return null;
  return d.toISOString();
}

/**
 * Static seed / no-DB fallback for /visa-bulletin, /api/visa-bulletin, and
 * Dataset/FAQ JSON-LD. Bump month/year here when DOS publishes the next
 * bulletin — loadLiveVisaBulletin prefers this seed over a staler DB row.
 * Keep Final Action Dates aligned with the official Visa Bulletin.
 *
 * November 2026 (Visa Bulletin No. 20, Vol. XI; demand through 5 Oct 2026):
 * No EB-5 movement versus October.
 * Final Action: set-asides Current everywhere; unreserved Current except
 * China 01 Dec 2016 and India 01 Dec 2023. Mexico and the Philippines Current.
 * Dates for Filing: China unreserved 01 Mar 2021, India unreserved 01 May 2024,
 * set-asides and all other countries Current.
 * USCIS: employment-based adjustment filings use Dates for Filing.
 */
export const currentVisaBulletin: VisaBulletinData = {
  month: "November",
  year: 2026,
  finalActionDates: [
    {
      country: "All Other Countries",
      unreserved: "Current",
      rural: "Current",
      highUnemployment: "Current",
      infrastructure: "Current",
    },
    {
      country: "China (Mainland)",
      unreserved: "01 Dec 2016",
      rural: "Current",
      highUnemployment: "Current",
      infrastructure: "Current",
    },
    {
      country: "India",
      unreserved: "01 Dec 2023",
      rural: "Current",
      highUnemployment: "Current",
      infrastructure: "Current",
    },
  ],
  datesForFiling: [
    {
      country: "All Other Countries",
      unreserved: "Current",
      rural: "Current",
      highUnemployment: "Current",
      infrastructure: "Current",
    },
    {
      country: "China (Mainland)",
      unreserved: "01 Mar 2021",
      rural: "Current",
      highUnemployment: "Current",
      infrastructure: "Current",
    },
    {
      country: "India",
      unreserved: "01 May 2024",
      rural: "Current",
      highUnemployment: "Current",
      infrastructure: "Current",
    },
  ],
  // Visible "Last updated" and Dataset dateModified. Ship date for the
  // November 2026 bulletin flip.
  lastUpdated: "2026-10-30",
};

export const USCIS_FILING_CHARTS_URL = "https://www.uscis.gov/visabulletininfo";

export type Eb5AdjustmentChart = "dates-for-filing" | "final-action";

/**
 * Which DOS chart USCIS told employment-based adjustment filers to use.
 * Keyed by the bulletin month the feed reports. A month with no entry
 * must not keep last month's choice.
 */
export const adjustmentChartByBulletin: Record<string, Eb5AdjustmentChart> = {
  "October 2026": "dates-for-filing",
  "November 2026": "dates-for-filing",
};

/** Visible summary under the question. Adds the no-movement sentence for November. */
export function buildCurrentBulletinSummary(
  bulletin: VisaBulletinData = currentVisaBulletin,
): string {
  const line = buildVisaBulletinAsOfLine(bulletin);
  if (bulletin.month === "November" && bulletin.year === 2026) {
    return `${line} No change from October.`;
  }
  return line;
}

export function adjustmentChartFor(bulletin: {
  month: string;
  year: number;
}): Eb5AdjustmentChart | null {
  return adjustmentChartByBulletin[`${bulletin.month} ${bulletin.year}`] ?? null;
}

/** Line above Table A and Table B. Month comes from the bulletin feed. */
export function buildWhichChartLine(bulletin: { month: string; year: number }): string {
  const when = `${bulletin.month} ${bulletin.year}`;
  const choice = adjustmentChartFor(bulletin);
  if (choice === "dates-for-filing" && when === "November 2026") {
    return "USCIS says employment-based adjustment of status filings use Dates for Filing in November 2026. If you are in the U.S. and filing to adjust status in November 2026, use Table B. Table A shows when a visa number can be issued.";
  }
  if (choice === "dates-for-filing") {
    return `USCIS says employment-based filings use Dates for Filing in ${when}. If you are in the U.S. and filing to adjust status in ${when}, use Table B. Table A shows when a visa number can be issued.`;
  }
  if (choice === "final-action") {
    return `USCIS chose Final Action Dates for employment-based filings in ${when}. If you are in the U.S. and filing to adjust status, use Table A. Table A shows when a visa number can be issued.`;
  }
  return `USCIS picks the chart for employment-based filings each month. Check its filing-charts page before you file in ${when}. Table A shows when a visa number can be issued. Table B is Dates for Filing.`;
}

/**
 * The short "USCIS chose Dates for Filing" line used to sit next to
 * buildWhichChartLine. Those two lines are now one sentence in
 * buildWhichChartLine, so this stays empty.
 */
export function buildDatesForFilingChoiceSentence(_bulletin: {
  month: string;
  year: number;
}): string | null {
  // The which-chart line already says which chart to use, including November.
  return null;
}

/** Masthead callouts for the October 2026 chart. Hidden once the seed month moves on. */
export const octoberBulletinMoves = [
  {
    id: "india",
    kicker: "India · unreserved",
    value: "01 Dec 2023",
    detail: "Final Action Date. Was Unavailable in September. Dates for Filing: 01 May 2024.",
  },
  {
    id: "china",
    kicker: "China · unreserved",
    value: "01 Mar 2021",
    detail: "Dates for Filing, up from 01 Dec 2016. Final Action Date still 01 Dec 2016.",
  },
  {
    id: "reserved",
    kicker: "Rural · HUA · infrastructure",
    value: "Current",
    detail: "Unchanged for every country, including China, India, Mexico, and the Philippines.",
  },
] as const;

const BULLETIN_MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
] as const;

/** DOS files October–December under the next fiscal-year folder. */
export function officialBulletinUrl(month: string, year: number): string {
  const index = BULLETIN_MONTHS.indexOf(month as (typeof BULLETIN_MONTHS)[number]);
  const fiscalYear = index >= 9 ? year + 1 : year;
  const slug = month.toLowerCase();
  return `https://travel.state.gov/content/travel/en/legal/visa-law0/visa-bulletin/${fiscalYear}/visa-bulletin-for-${slug}-${year}.html`;
}

export const bulletinHistory = [
  {
    month: "November 2026",
    changes:
      "No EB-5 movement versus October. Reserved Rural/HUA/Infrastructure remain Current for all countries. China unreserved holds 01 Dec 2016 (Dates for Filing 01 Mar 2021). India unreserved holds 01 Dec 2023 (Dates for Filing 01 May 2024).",
  },
  {
    month: "October 2026",
    changes:
      "India unreserved reopens: Final Action Date 01 Dec 2023 (was Unavailable) and Dates for Filing 01 May 2024. China Dates for Filing advances to 01 Mar 2021; China Final Action Date holds 01 Dec 2016. Reserved Rural/HUA/Infrastructure remain Current for all countries.",
  },
  {
    month: "September 2026",
    changes:
      "No movement versus August. Reserved Rural/HUA/Infrastructure remain Current for all countries. China unreserved holds 01 Dec 2016. India unreserved remains Unavailable.",
  },
  {
    month: "August 2026",
    changes:
      "No movement versus July. Reserved Rural/HUA/Infrastructure remain Current for all countries. China unreserved holds 01 Dec 2016. India unreserved remains Unavailable.",
  },
];

export type VisaBulletinFaq = { question: string; answer: string };

/**
 * Page FAQ + FAQPage JSON-LD. November 2026 copy, locked to the published
 * Department of State tables. `bulletin` is accepted so callers stay wired
 * to live/seed payloads; the published FAQ set is month-locked until the
 * next bulletin flip.
 */
export function buildVisaBulletinFaqs(
  _bulletin: VisaBulletinData = currentVisaBulletin,
): VisaBulletinFaq[] {
  return [
    { question: VISA_BULLETIN_QUESTION, answer: VISA_BULLETIN_ANSWER },
    {
      question: "What is current on the November 2026 EB-5 visa bulletin?",
      answer:
        "Rural, high-unemployment, and infrastructure set-asides are Current for all countries. Unreserved is Current for most countries, including Mexico and the Philippines. China unreserved Final Action Date is 01 December 2016. India unreserved Final Action Date is 01 December 2023.",
    },
    {
      question: "What is China's unreserved final action date?",
      answer:
        "01 December 2016 on the November 2026 bulletin. Dates for Filing for China unreserved is 01 March 2021. Set-asides are a different chart and are Current.",
    },
    {
      question: "Is India current for unreserved EB-5?",
      answer:
        "No. India unreserved is no longer Unavailable. The November 2026 Final Action Date is 01 December 2023, and Dates for Filing is 01 May 2024. Numbers are available only if the priority date is earlier than the chart you are reading. Set-asides stay Current, including India.",
    },
  ];
}

export function interpretPriorityDate(date: string, _category: string): string {
  if (date === "Current") {
    return "Your category is Current for this month: visa numbers are available in that category for that country on the Final Action Date chart. That is bulletin availability — not a green-card date and not a promise the next chart looks the same.";
  }

  if (/^unavailable$/i.test(date)) {
    return "Unavailable means visa numbers are not available in that category for that country this month. Reserved and unreserved are different lines — do not mix them.";
  }

  return `A Final Action Date of ${date} means numbers are available only if the priority date is earlier than that date. Those are bulletin mechanics — not a green-card date.`;
}
